import { Box, Typography, Paper, Button, Divider, Alert, Chip, Card, CardContent } from '@mui/material';
import { ArrowBack, Edit, CheckCircle, Cancel, Delete, Autorenew, History } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ConfirmScadenzaDialog from '../../components/scadenze/ConfirmScadenzaDialog';
import TimelineComponent from '../../components/timeline/TimelineComponentMock';
import scadenzeService from '../../services/scadenzeService';
import { StatoScadenza } from '../../types/scadenza.types';
import { formatReminderDisplay } from '../../utils/reminderUtils';
import type { StoricoRinnoviResponse } from '../../types/scadenza.types';
import { useState } from 'react';
import { getErrorMessage } from '../../utils/api';

const ScadenzaDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [confirmScadenzaDialog, setConfirmScadenzaDialog] = useState(false);

  const { data: scadenza, isLoading } = useQuery({
    queryKey: ['scadenza', id],
    queryFn: () => scadenzeService.getById(id!)
  });

  // Query per storico rinnovi (solo se scadenza ha rinnovi)
  const { data: storicoRinnovi, isLoading: isLoadingStorico } = useQuery({
    queryKey: ['storico-rinnovi', id],
    queryFn: () => scadenzeService.getStoricoRinnovi(id!),
    enabled: !!scadenza && (
      !!scadenza.storicoRinnovi?.length || 
      !!scadenza.scadenzaPadreId ||
      !!scadenza.tipologia?.periodicitaRinnovoAnni
    )
  });

  // Helper per invalidare tutte le query dashboard dopo operazioni sulle scadenze
  const invalidateAllScadenzeQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['scadenza', id] });
    queryClient.invalidateQueries({ queryKey: ['scadenze'] });
    queryClient.invalidateQueries({ queryKey: ['scadenze-stats'] });
    queryClient.invalidateQueries({ queryKey: ['scadenze-mese-corrente'] });
  };

  const completeMutation = useMutation({
    mutationFn: (data: { numeroFattura: string; noteRinnovo?: string; nuovaDataScadenza?: string }) => scadenzeService.complete(id!, data),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setConfirmScadenzaDialog(false);
      enqueueSnackbar('Scadenza completata con successo', { variant: 'success' });
      navigate('/scadenze');
    },
    onError: (error: unknown) => {
      console.error('Errore durante il completamento della scadenza:', error);
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il completamento della scadenza'), { variant: 'error' });
    }
  });

  const cancelMutation = useMutation({
    mutationFn: () => scadenzeService.cancel(id!),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      enqueueSnackbar('Scadenza annullata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'annullamento'), { variant: 'error' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => scadenzeService.delete(id!),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      enqueueSnackbar('Scadenza eliminata con successo', { variant: 'success' });
      navigate('/scadenze');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento scadenza..." />
      </AppLayout>
    );
  }

  if (!scadenza) {
    return (
      <AppLayout>
        <Typography>Scadenza non trovata</Typography>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/scadenze')} variant="outlined">
              Indietro
            </Button>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Dettaglio Scadenza
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {scadenza.stato !== StatoScadenza.COMPLETATA && scadenza.stato !== StatoScadenza.ANNULLATA && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => navigate(`/scadenze/${id}/edit`)}
                >
                  Modifica
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setConfirmScadenzaDialog(true)}
                  disabled={completeMutation.isPending}
                >
                  Completa
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Cancel />}
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                >
                  Annulla
                </Button>
              </>
            )}
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteDialog(true)}
              disabled={deleteMutation.isPending}
            >
              Elimina
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          <Box>
            {/* Informazioni Rinnovo */}
            {(scadenza.tipologia?.periodicitaRinnovoAnni || scadenza.dataScadenzaOriginale || (scadenza.giorniRitardoRinnovo && scadenza.giorniRitardoRinnovo > 0)) && (
              <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'secondary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Autorenew color="secondary" />
                  <Typography variant="h6" fontWeight={600} color="secondary.main">
                    Informazioni Rinnovo
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {scadenza.tipologia?.periodicitaRinnovoAnni && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Periodicità Rinnovo
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {scadenza.tipologia.periodicitaRinnovoAnni} anni
                      </Typography>
                    </Box>
                  )}
                  {scadenza.dataScadenzaOriginale && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Data Scadenza Originale
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {format(new Date(scadenza.dataScadenzaOriginale), 'dd MMMM yyyy', { locale: it })}
                      </Typography>
                    </Box>
                  )}
                  {scadenza.giorniRitardoRinnovo && scadenza.giorniRitardoRinnovo > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Ritardo Rinnovo
                      </Typography>
                      <Chip
                        label={`${scadenza.giorniRitardoRinnovo} giorni`}
                        color={scadenza.giorniRitardoRinnovo > 30 ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  )}
                  {scadenza.scadenzaPadreId && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tipologia
                      </Typography>
                      <Chip
                        label="Scadenza Rinnovata"
                        color="info"
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
                {!scadenza.tipologia?.periodicitaRinnovoAnni && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Questa tipologia di scadenza non è rinnovabile automaticamente.
                    </Typography>
                  </Alert>
                )}
              </Paper>
            )}
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Informazioni Generali
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {scadenza.cliente?.cognome} {scadenza.cliente?.nome}
                  </Typography>
                </Box>
                {(scadenza.cliente?.personaGiuridica ? scadenza.cliente?.partitaIva : scadenza.cliente?.codiceFiscale) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {scadenza.cliente?.personaGiuridica ? 'P.IVA' : 'Codice Fiscale'}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {scadenza.cliente!.personaGiuridica ? scadenza.cliente!.partitaIva : scadenza.cliente!.codiceFiscale}
                    </Typography>
                  </Box>
                )}
                {scadenza.cliente?.email && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {scadenza.cliente.email}
                    </Typography>
                  </Box>
                )}
                {scadenza.cliente?.telefono && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Telefono
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {scadenza.cliente.telefono}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Tipologia
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {scadenza.tipologia?.nome}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data Scadenza
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {format(new Date(scadenza.dataScadenza), 'dd MMMM yyyy', { locale: it })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Giorni Preavviso
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatReminderDisplay(scadenza.giorniPreavviso, false, false)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Stato
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip stato={scadenza.stato} size="medium" />
                  </Box>
                </Box>
                {scadenza.numeroFattura && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Numero Fattura
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {scadenza.numeroFattura}
                    </Typography>
                  </Box>
                )}
                {scadenza.note && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Note
                    </Typography>
                    <Typography variant="body1">{scadenza.note}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          <Box>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Assegnazione
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Assegnato a
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {scadenza.assegnatoA
                    ? `${scadenza.assegnatoA.nome} ${scadenza.assegnatoA.cognome}`
                    : 'Non assegnato'}
                </Typography>
              </Box>
            </Paper>
            
            {/* Storico Rinnovi */}
            {storicoRinnovi && storicoRinnovi.totaleRinnovi > 0 && (
              <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <History color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Storico Rinnovi
                  </Typography>
                  <Chip 
                    label={`${storicoRinnovi.totaleRinnovi} rinnov${storicoRinnovi.totaleRinnovi === 1 ? 'o' : 'i'}`}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {/* Informazioni Scadenza Originale */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Scadenza originale:</strong> {storicoRinnovi.scadenzaOriginale.titolo}
                    <br />
                    <strong>Data originale:</strong> {format(new Date(storicoRinnovi.scadenzaOriginale.dataScadenzaOriginale), 'dd MMMM yyyy', { locale: it })}
                  </Typography>
                </Alert>
                
                {/* Lista Rinnovi */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {storicoRinnovi.storicoRinnovi.map((rinnovo, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Rinnovo #{index + 1}
                            </Typography>
                            {rinnovo.giorniRitardo > 0 && (
                              <Chip
                                label={`+${rinnovo.giorniRitardo}gg`}
                                color={rinnovo.giorniRitardo > 30 ? 'error' : 'warning'}
                                size="small"
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(rinnovo.dataRinnovo), 'dd/MM/yyyy HH:mm', { locale: it })}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Scadenza Precedente
                            </Typography>
                            <Typography variant="body2">
                              {format(new Date(rinnovo.dataScadenzaPrecedente), 'dd MMMM yyyy', { locale: it })}
                            </Typography>
                          </Box>
                          {rinnovo.numeroFattura && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Numero Fattura
                              </Typography>
                              <Typography variant="body2">
                                {rinnovo.numeroFattura}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        {rinnovo.note && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Note
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {rinnovo.note}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                
                {/* Scadenze Rinnovate Attive */}
                {storicoRinnovi.scadenzeRinnovate.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Scadenze Attive della Serie
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {storicoRinnovi.scadenzeRinnovate
                        .filter(s => s.stato !== StatoScadenza.COMPLETATA)
                        .map(scadenzaRinnovata => (
                        <Card key={scadenzaRinnovata.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {format(new Date(scadenzaRinnovata.dataScadenza), 'dd MMMM yyyy', { locale: it })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {scadenzaRinnovata.titolo}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <StatusChip stato={scadenzaRinnovata.stato} size="small" />
                                <Button
                                  size="small"
                                  onClick={() => navigate(`/scadenze/${scadenzaRinnovata.id}`)}
                                >
                                  Visualizza
                                </Button>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* Timeline Eventi */}
            <Paper sx={{ p: 0, mt: 3, height: '600px', position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3, pb: 0 }}>
                <History color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Timeline Eventi
                </Typography>
              </Box>
<TimelineComponent
                scadenzaId={id!}
                scadenzaTitolo={scadenza?.titolo}
                maxHeight="550px"
                showFilters={true}
                realTimeUpdates={true}
                allowAddNotes={true}
                showExportButton={true}
              />
            </Paper>
          </Box>
        </Box>

        <ConfirmScadenzaDialog
          open={confirmScadenzaDialog}
          scadenza={scadenza}
          onConfirm={(data) => completeMutation.mutate(data)}
          onCancel={() => setConfirmScadenzaDialog(false)}
          loading={completeMutation.isPending}
        />

        <ConfirmDialog
          open={deleteDialog}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questa scadenza? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setDeleteDialog(false)}
          loading={deleteMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default ScadenzaDetailPage;
